import React from 'react';
import Link from 'next/link';
import omit from 'lodash/omit';
import GeneralChart from '../GeneralChart';
import GeneralMenu from '../../navigation/GeneralMenu';
import SvgCulture from '@/components/seo/job-title/position-overview/SvgCulture';
import Button from '@mui/material/Button';
import { AuthContext } from '@/context/create-context';
import { convertToStrList, isEmpty, isNoEmptyArray } from '@/utils/seo-content';
import {
  CULTURAL_QUIZ_ROUTER_PATH,
  CULTURAL_ROUTER_PATH,
} from '@/constants/router-path';

interface Props {
  show: boolean;
  teamCultureData: Record<string, any>;
  matchingData: Record<string, any>;
  keyTeamData: Record<string, any>;
}

const COMPANY_CULTURE_ITEMS = [
  {
    name: '此公司的团队文化和工作方式是怎样的？— 以下是我们为你整理的信息',
    id: 'company-culture_team_culture',
  },
  {
    name: '你适合此公司的工作方式吗？',
    id: 'company-culture_team_work-style',
  },
  {
    name: '哪些团队值得重点关注？ — 内部信息整理',
    id: 'company-culture_team_important_team',
  },
];

const CultureTabPanel = (props: Props) => {
  const {
    show,
    teamCultureData = {},
    matchingData = {},
    keyTeamData = {},
  } = props;
  const { isLogin } = React.useContext(AuthContext);
  const coreValues = convertToStrList(
    teamCultureData['核心价值观']?.['价值观条目'],
    ['标题', '说明'],
  );
  const teamEnvironment = convertToStrList(
    teamCultureData['团队环境']?.['团队特征条目'],
    ['标题', '说明'],
  );
  const workExperience = convertToStrList(
    teamCultureData['工作体验']?.['体验条目'],
    ['标题', '说明'],
  );
  const menuList = [COMPANY_CULTURE_ITEMS[2]];
  if (!isEmpty(matchingData['tip'])) {
    menuList.unshift(COMPANY_CULTURE_ITEMS[1]);
  }
  if (!isEmpty(teamCultureData['导语'])) {
    menuList.unshift(COMPANY_CULTURE_ITEMS[0]);
  }
  const cultureQuizEl = (
    <div className="my-8 px-12 py-4 rounded-lg flex justify-between bg-[linear-gradient(94.46deg,rgba(231,28,136,0.04)_4.39%,rgba(141,19,211,0.04)_94.78%)]">
      <div>
        <p className="flex items-center font-semibold">
          <SvgCulture className="shrink-0 mr-3" />
          <span>企业文化匹配测试</span>
        </p>
        <p className="text-xs mt-2 text-web-grey-600">
          帮你找到最适合的企业类型和目标公司
        </p>
      </div>
      <Button
        variant="contained"
        className="!w-40 !h-[42px] !bg-white !text-primary hover:!bg-web-neutral-300"
        LinkComponent={Link}
        href={isLogin ? CULTURAL_QUIZ_ROUTER_PATH : CULTURAL_ROUTER_PATH}
      >
        立即测试
      </Button>
    </div>
  );

  return (
    <div hidden={!show} className="relative">
      <GeneralMenu menuList={menuList} />
      <h2 className="text-xl font-semibold">
        作为求职者，如何判断团队文化是否匹配
      </h2>
      {!isEmpty(teamCultureData['导语']) &&
        !/文化[^。！？；]*?不明确/g.test(teamCultureData['导语']) && (
          <>
            <GeneralChart
              id={COMPANY_CULTURE_ITEMS[0].id}
              title={COMPANY_CULTURE_ITEMS[0].name}
              tip={teamCultureData['tip']}
              tipBgColor="bg-web-purple-100"
            >
              <p className="mb-2">{teamCultureData['导语']}</p>
              {isNoEmptyArray(coreValues) && (
                <>
                  <h4 className="font-semibold mb-2">核心价值观</h4>
                  <ul className="list-inside !ml-2">
                    {coreValues.map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
              {isNoEmptyArray(teamEnvironment) && (
                <>
                  <h4 className="font-semibold mb-2 mt-4">团队环境</h4>
                  <ul className="list-inside !ml-2">
                    {teamEnvironment.map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
              {isNoEmptyArray(workExperience) && (
                <>
                  <h4 className="font-semibold mb-2 mt-4">工作体验</h4>
                  <ul className="list-inside !ml-2">
                    {workExperience.map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
            </GeneralChart>
            {cultureQuizEl}
          </>
        )}
      {!isEmpty(matchingData['tip']) && (
        <>
          <GeneralChart
            id={COMPANY_CULTURE_ITEMS[1].id}
            title={COMPANY_CULTURE_ITEMS[1].name}
            tip={matchingData['tip']}
            tipBgColor="bg-web-purple-100"
          >
            <h4 className="font-semibold mb-2">高度适配的特质</h4>
            <ul className="list-inside !ml-2">
              {(matchingData['高度适配的特质']?.['适配要点'] || []).map(
                (item: string, index: number) => (
                  <li key={index}>{item}</li>
                ),
              )}
            </ul>
            <h4 className="font-semibold mb-2 mt-4">潜在的不适配因素</h4>
            <ul className="list-inside !ml-2">
              {(matchingData['潜在的不适配因素']?.['不适配表现'] || []).map(
                (item: string, index: number) => (
                  <li key={index}>{item}</li>
                ),
              )}
            </ul>
            <h4 className="font-semibold mb-2 mt-4">高阶生存法则</h4>
            <p className="mb-2">
              {matchingData['高阶生存法则']?.['总体说明'] || ''}
            </p>
            <ul className="list-inside !ml-2">
              {(matchingData['高阶生存法则']?.['行动策略'] || []).map(
                (item: string, index: number) => (
                  <li key={index}>{item}</li>
                ),
              )}
            </ul>
          </GeneralChart>
          {isEmpty(teamCultureData['导语']) && cultureQuizEl}
        </>
      )}
      <GeneralChart
        id={COMPANY_CULTURE_ITEMS[2].id}
        title={COMPANY_CULTURE_ITEMS[2].name}
        tip={keyTeamData['tip']}
        tipBgColor="bg-web-purple-100"
      >
        {Object.keys(omit(keyTeamData, 'tip')).map(
          (key: string, index: number) => (
            <React.Fragment key={index}>
              <h4 className="font-semibold mb-2 mt-4">{key}</h4>
              <ul className="list-inside !ml-2">
                <li>技术栈：{keyTeamData[key]['关键能力要素'].join('')}</li>
                <li>项目特点：{keyTeamData[key]['项目特征'].join('')}</li>
                <li>成长价值：{keyTeamData[key]['成长机会'].join('')}</li>
                <li>压力指数：{keyTeamData[key]['压力与挑战'].join('')}</li>
                <li>推荐人群：{keyTeamData[key]['推荐加入人群']}</li>
              </ul>
            </React.Fragment>
          ),
        )}
      </GeneralChart>
      {isEmpty(teamCultureData['导语']) &&
        isEmpty(matchingData['tip']) &&
        cultureQuizEl}
    </div>
  );
};

export default CultureTabPanel;
